import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, Form, Label, Spinner } from "reactstrap";
import { Icon, Col, Button } from "../../../components/Component";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { uploadFile } from "../../../api/upload-file";
import {
  addMiLibrary,
  updateMiLibrary,
  searchMiLibraryCategories,
  getAppleIapProducts,
} from "../../../api/mi-library";
import { useDispatch } from "react-redux";
import {
  addMiLibrarySuccess,
  updateMiLibrarySuccess,
} from "../../../slice/mi-library";
import { toast } from "react-toastify";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import AsyncSelect from "react-select/async";

const schema = yup
  .object({
    name: yup.string().required("Name is required"),
    image: yup.string().required("Image is required"),
    cost: yup
      .number()
      .typeError("Cost must be a number")
      .min(0, "Cost cannot be negative")
      .default(0),
  })
  .required();

// MiLibraryCategory.name may be either a string (public localized endpoint,
// legacy data) or the new { en, ar? } object — render whichever is present.
const readCategoryLabel = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name.en ?? name.ar ?? "";
};

const mapCategoryOptions = (categories = []) =>
  categories.map((cat) => ({
    label:
      readCategoryLabel(cat?.name) ||
      cat?.title ||
      cat?.label ||
      "Untitled",
    value: cat?._id ?? cat?.id ?? cat?.value ?? readCategoryLabel(cat?.name),
    raw: cat,
  }));

const mapTagOptions = (tags = []) =>
  (tags ?? []).map((tag) => ({
    label: tag,
    value: tag,
  }));

const mapIapProductOptions = (products = []) =>
  (products ?? []).map((product) => {
    const rawPrice =
      product?.price ??
      product?.cost ??
      product?.amount ??
      product?.priceAmount ??
      product?.priceValue ??
      product?.amountMicros;
    const price =
      typeof rawPrice === "number" && Number.isFinite(rawPrice)
        ? rawPrice
        : Number(rawPrice);
    const currency =
      product?.currency ??
      product?.currencyCode ??
      product?.priceCurrency ??
      "";
    const localizedPrice =
      product?.localizedPrice ??
      product?.displayPrice ??
      product?.priceString ??
      "";
    const productId =
      product?.productId ??
      product?.id ??
      product?.sku ??
      product?.identifier ??
      "";
    const priceLabel = localizedPrice
      ? localizedPrice
      : Number.isFinite(price)
      ? `${price}${currency ? ` ${currency}` : ""}`
      : "";
    const formatCurrency = (value) =>
      Number.isFinite(value) ? `$${value.toFixed(2)}` : "";

    const label = priceLabel ? `${priceLabel}` : formatCurrency(price);

    return {
      label: label || formatCurrency(Number(price) || 0),
      value: productId || `price-${priceLabel || price || "unknown"}`,
      cost: Number.isFinite(price) ? price : 0,
      raw: product,
    };
  });

const AddMiLibraryModal = ({ isOpen, closeModal, item }) => {
  const {
    watch,
    setValue,
    reset,
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      image: "",
      cost: 0,
    },
  });

  const dispatch = useDispatch();
  const [isUploading, setUploading] = useState(false);
  const [tags, setTags] = useState([]);
  const [category, setCategory] = useState(null);
  const [iapProductOptions, setIapProductOptions] = useState([]);
  const [isLoadingIapProducts, setLoadingIapProducts] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(null);

  // Apple IAP never includes a $0 product, so expose an explicit "Free"
  // option that lets the admin reset the price back to 0.
  const FREE_PRICE_OPTION = useMemo(
    () => ({ label: "Free (0)", value: "free-0", cost: 0, raw: null }),
    []
  );
  const priceOptions = useMemo(
    () => [FREE_PRICE_OPTION, ...iapProductOptions],
    [FREE_PRICE_OPTION, iapProductOptions]
  );

  const image = watch("image");

  useEffect(() => {
    if (isOpen) {
      reset({
        name: item?.name || "",
        image: item?.image || "",
        cost: item?.cost ?? 0,
      });
      setTags(item?.tags ?? []);
      setCategory(
        item?.category?._id
          ? {
              label:
                readCategoryLabel(item?.category?.name) ?? item?.category?._id,
              value: item?.category?._id,
              raw: item?.category,
            }
          : null
      );
    }
  }, [isOpen, reset, item]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchIapProducts = async () => {
      setLoadingIapProducts(true);
      try {
        const res = await getAppleIapProducts();
        const products =
          res?.docs || res?.items || res?.products || res?.data || res || [];
        setIapProductOptions(mapIapProductOptions(products));
      } catch (error) {
        toast.error(error?.message || "Load price list failed");
        setIapProductOptions([]);
      }
      setLoadingIapProducts(false);
    };

    fetchIapProducts();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const itemCost =
      item?.cost !== undefined && item?.cost !== null ? Number(item.cost) : null;
    const matched =
      itemCost !== null
        ? priceOptions.find((opt) => Number(opt.cost) === itemCost)
        : null;
    const fallbackOption =
      itemCost !== null && !matched
        ? {
            label: `${itemCost}`,
            value: `custom-${itemCost}`,
            cost: itemCost,
            raw: null,
          }
        : null;

    const nextSelected = matched || fallbackOption || null;
    setSelectedPrice(nextSelected);
    setValue("cost", nextSelected?.cost ?? itemCost ?? 0);
  }, [isOpen, priceOptions, item?.cost, setValue]);

  const handleUpload = async (payload) => {
    setUploading(true);
    try {
      const res = await uploadFile(payload);
      setValue("image", res?.fileUrl ?? res?.url ?? res?.path ?? "");
      toast.success("Upload image successfully");
    } catch (error) {
      toast.error(error?.message || "Upload failed");
    }
    setUploading(false);
  };

  const loadCategoryOptions = useMemo(
    () => async (inputValue) => {
      try {
        const res = await searchMiLibraryCategories({
          q: inputValue,
          limit: 20,
        });
        const categories =
          res?.docs || res?.items || res?.categories || res?.data || res || [];
        return mapCategoryOptions(categories);
      } catch (error) {
        toast.error(error?.message || "Search category failed");
        return [];
      }
    },
    []
  );

  const onSubmit = async (data) => {
    try {
      const categoryId = category?.value ?? null;

      const payload = {
        name: data.name,
        image: data.image,
        cost: Number(data.cost ?? 0),
        tags,
        category: categoryId,
      };

      if (item?._id) {
        const res = await updateMiLibrary(item._id, payload);
        dispatch(updateMiLibrarySuccess(res));
        toast.success("Update item successfully");
      } else {
        const res = await addMiLibrary(payload);
        dispatch(addMiLibrarySuccess(res));
        toast.success("Add item successfully");
      }
      closeModal();
    } catch (error) {
      toast.error(
        error?.message ||
          `${item?._id ? "Update" : "Add"} item failed`
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => closeModal()}
      className="modal-dialog-centered"
      size="lg"
    >
      <ModalBody>
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            closeModal();
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <div className="p-2">
          <h5 className="title">
            {item?._id ? "Edit Item" : "Add Item"}
          </h5>
          <div className="mt-4">
            <Form
              className="row gy-4 form-validate"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <Col md="12">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <div className="form-control-wrap">
                    <input
                      className="form-control"
                      type="text"
                      {...register("name")}
                      placeholder="Enter name"
                    />
                    {errors.name && (
                      <span className="invalid">{errors.name.message}</span>
                    )}
                  </div>
                </div>
              </Col>

              <Col sm="12">
                <div className="form-group">
                  <label className="form-label">Image</label>
                  <div className="form-control-wrap">
                    <input
                      type="file"
                      id="mi-library-image"
                      multiple={false}
                      hidden
                      accept="image/png, image/gif, image/jpeg"
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          const file = e.target.files[0];
                          const payload = new FormData();
                          payload.append("image", file);
                          handleUpload(payload);
                        }
                      }}
                    />
                    <label htmlFor="mi-library-image">
                      {isUploading ? (
                        <div className="w-150px h-150px d-flex align-items-center justify-content-center border">
                          <Spinner />
                        </div>
                      ) : (
                        <div className="w-150px h-150px d-flex align-items-center justify-content-center border">
                          {image ? (
                            <img
                              src={image}
                              width={150}
                              height={150}
                              alt="mi-library"
                              style={{
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Icon
                              name="upload"
                              style={{
                                fontSize: "30px",
                              }}
                            />
                          )}
                        </div>
                      )}
                    </label>
                  </div>
                  {errors.image && !image && (
                    <span className="invalid-message">
                      {errors.image.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md="12">
                <div className="form-group">
                  <Label className="form-label" htmlFor="cost">
                    Cost
                  </Label>
                  <div className="form-control-wrap">
                    <Select
                      inputId="cost"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isLoading={isLoadingIapProducts}
                      options={priceOptions}
                      value={selectedPrice}
                      onChange={(option) => {
                        setSelectedPrice(option);
                        setValue("cost", option?.cost ?? 0, {
                          shouldValidate: true,
                        });
                      }}
                      placeholder="Select price"
                      isClearable
                      noOptionsMessage={() =>
                        isLoadingIapProducts ? "Loading..." : "No prices"
                      }
                    />
                    <input type="hidden" {...register("cost")} />
                    {errors.cost && (
                      <span className="invalid">{errors.cost.message}</span>
                    )}
                  </div>
                </div>
              </Col>

              <Col md="12">
                <div className="form-group">
                  <Label className="form-label">Tags</Label>
                  <CreatableSelect
                    isMulti
                    className="react-select-container"
                    classNamePrefix="react-select"
                    onChange={(options) =>
                      setTags(options?.map((opt) => opt.value) ?? [])
                    }
                    value={mapTagOptions(tags)}
                    placeholder="Enter tags and press Enter"
                    formatCreateLabel={(value) => `Add "${value}"`}
                  />
                </div>
              </Col>

              <Col md="12">
                <div className="form-group">
                  <Label className="form-label">Category</Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                  className="react-select-container"
                  classNamePrefix="react-select"
                  loadOptions={loadCategoryOptions}
                  onChange={(option) => setCategory(option)}
                  value={category}
                  placeholder="Search or create category"
                  // Only select existing categories for consistency
                  isClearable
                />
                </div>
              </Col>

              <Col size="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button color="primary" size="md" type="submit">
                      {isSubmitting ? (
                        <Spinner size="sm" color="light" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        closeModal();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Form>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AddMiLibraryModal;
